import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiBaseUrl } from '../utils/axios';
import PageMeta from './PageMeta';
import './LandingPage.css';

// Zet elke YouTube-URL om naar embed-formaat
const toEmbedUrl = (url) => {
  if (!url) return '';
  try {
    const u = new URL(url);
    // Al embed-formaat
    if (u.pathname.startsWith('/embed/')) return url;
    // https://www.youtube.com/watch?v=VIDEO_ID
    const vid = u.searchParams.get('v');
    if (vid) return `https://www.youtube.com/embed/${vid}`;
    // https://youtu.be/VIDEO_ID
    if (u.hostname === 'youtu.be') return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    // https://www.youtube.com/shorts/VIDEO_ID
    if (u.pathname.startsWith('/shorts/')) return `https://www.youtube.com/embed/${u.pathname.split('/')[2]}`;
  } catch (_) {}
  return url;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [videoUrls, setVideoUrls] = useState({ videoUrl1: '', videoUrl2: '' });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/auth/status`)
      .then(r => r.json())
      .then(data => setVideoUrls({
        videoUrl1: toEmbedUrl(data.videoUrl1 || ''),
        videoUrl2: toEmbedUrl(data.videoUrl2 || '')
      }))
      .catch(() => {});
  }, []);

  return (
    <div className="landing">
      <PageMeta
        title="Professionele offertes in minuten"
        description="Maak professionele offertes in minuten. OfferteRunner koppelt met Odoo en biedt AI-teksten. Offerte software voor ZZP en MKB. Offerte maken online."
      />
      {/* Hero */}
      <header className="landing-header">
        <nav className="landing-nav">
          <img src="/logo-offerterunner.png" alt="OfferteRunner" className="landing-logo-img" />
          <button
            type="button"
            className="landing-hamburger"
            onClick={() => setMobileNavOpen(o => !o)}
            aria-label="Menu"
            aria-expanded={mobileNavOpen}
          >
            <span className={`hamburger-icon${mobileNavOpen ? ' open' : ''}`} />
          </button>
          <div className={`landing-nav-links${mobileNavOpen ? ' mobile-open' : ''}`}>
            <a href="#features" onClick={() => setMobileNavOpen(false)}>Functionaliteiten</a>
            <a href="#how-it-works" onClick={() => setMobileNavOpen(false)}>Hoe het werkt</a>
            <a href="#odoo" onClick={() => setMobileNavOpen(false)}>Odoo</a>
            <a href="#api" onClick={() => setMobileNavOpen(false)}>API</a>
            <Link to="/blog" onClick={() => setMobileNavOpen(false)}>Blog</Link>
            <a href="#pricing" onClick={() => setMobileNavOpen(false)}>Prijzen</a>
            {user ? (
              <button className="btn-landing-cta" onClick={() => { navigate('/'); setMobileNavOpen(false); }}>
                Naar app
              </button>
            ) : (
              <>
                <button
                  className="btn-landing-cta btn-landing-demo"
                  onClick={() => {
                    navigate('/demo');
                    setMobileNavOpen(false);
                  }}
                >
                  Demo
                </button>
                <button className="btn-landing-cta" onClick={() => { navigate('/login'); setMobileNavOpen(false); }}>
                  Inloggen
                </button>
              </>
            )}
          </div>
        </nav>

        <div className="hero">
          <div className="hero-content">
            <div className="hero-badge">Gebouwd op Odoo</div>
            <h1>
              Professionele offertes.
              <br />
              <span className="hero-accent">In minuten, niet uren.</span>
            </h1>
            <p className="hero-subtitle">
              OfferteRunner is de slimme offertetool die jouw Odoo-producten, AI-gegenereerde teksten en
              klantgegevens combineert tot een prachtige offerte-PDF. Volledig geautomatiseerd, direct
              gesynchroniseerd met je Odoo CRM.
            </p>
            <div className="hero-actions">
              <button className="btn-landing-primary" onClick={() => navigate(user ? '/' : '/login')}>
                {user ? 'Naar de app' : 'Start met offertes maken'}
              </button>
              {!user && (
                <button
                  className="btn-landing-demo-hero"
                  onClick={() => navigate('/demo')}
                >
                  Probeer de demo
                </button>
              )}
              <a href="#how-it-works" className="btn-landing-secondary">
                Bekijk hoe het werkt
              </a>
            </div>
          </div>
          <div className="hero-visual">
            <a href="/voorbeeldofferte.pdf" target="_blank" rel="noopener noreferrer" className="hero-pdf-showcase">
              <div className="hero-pdf-frame">
                <img
                  src="/voorbeeldofferte-preview.png"
                  alt="Voorbeeld offerte gemaakt met OfferteRunner"
                  className="hero-pdf-image"
                />
                <div className="hero-pdf-overlay">
                  <span className="hero-pdf-dl-icon">&#x2913;</span>
                  Bekijk voorbeeld-PDF
                </div>
              </div>
              <p className="hero-pdf-caption">
                Voorbeeld van een offerte gegenereerd met OfferteRunner
              </p>
            </a>
          </div>
        </div>
      </header>

      {/* Social proof bar */}
      <section className="trust-bar">
        <div className="trust-bar-inner">
          <div className="trust-item">
            <strong>Odoo</strong> als basis
          </div>
          <div className="trust-divider" />
          <div className="trust-item">
            <strong>AI</strong>-teksten
          </div>
          <div className="trust-divider" />
          <div className="trust-item">
            <strong>PDF</strong> in seconden
          </div>
          <div className="trust-divider" />
          <div className="trust-item">
            <strong>API</strong> voor integratie
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="landing-section">
        <div className="landing-container">
          <div className="section-header">
            <h2>Alles wat je nodig hebt voor professionele offertes</h2>
            <p>Van productcatalogus tot PDF-download, volledig geintegreerd met Odoo.</p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📦</div>
              <h3>Producten uit Odoo</h3>
              <p>
                Synchroniseer je volledige productcatalogus rechtstreeks vanuit Odoo.
                Selecteer producten, pas aantallen aan en voeg vrije regels toe.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3>AI-gegenereerde teksten</h3>
              <p>
                Laat ChatGPT overtuigende, wervende teksten schrijven voor je offerte.
                Aangepast aan jouw huisstijl en branche via een eigen prompt.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">👤</div>
              <h3>Klanten &amp; contacten</h3>
              <p>
                Zoek bestaande klanten uit Odoo of maak er direct een nieuw aan.
                Contactpersonen worden automatisch gekoppeld.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📄</div>
              <h3>Prachtige PDF-offertes</h3>
              <p>
                Genereer professionele PDF's met jouw logo, bedrijfsgegevens en
                een consistente opmaak. Klaar om te versturen.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <h3>Odoo-synchronisatie</h3>
              <p>
                Offertes worden als product aangemaakt in Odoo met de PDF als bijlage.
                Eén klik en alles staat in je CRM.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🔌</div>
              <h3>Inkomende aanvragen via API</h3>
              <p>
                Ontvang offerte-aanvragen van je website of externe systemen via een
                REST API. Verwerk ze met één klik vanuit het dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="landing-section alt-bg">
        <div className="landing-container">
          <div className="section-header">
            <h2>Hoe werkt OfferteRunner?</h2>
            <p>In 5 eenvoudige stappen van aanvraag naar professionele offerte.</p>
          </div>

          <div className="steps-timeline">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Producten selecteren</h3>
                <p>Kies producten uit je Odoo-catalogus, stel aantallen in en voeg optioneel een beschrijving toe.</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>AI-informatie verzamelen</h3>
                <p>Geef context over de klant en het project. De AI genereert vervolgens wervende teksten.</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Klant &amp; contact kiezen</h3>
                <p>Selecteer een bestaande klant uit Odoo of maak een nieuwe aan. Contactpersoon wordt automatisch gekoppeld.</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Offerte samenstellen</h3>
                <p>Pas de teksten aan, kies een standaardinhoud-template en bekijk het voorbeeld.</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>PDF genereren &amp; synchroniseren</h3>
                <p>Download de PDF en synchroniseer automatisch met Odoo. Klaar om te versturen vanuit je CRM.</p>
              </div>
            </div>
          </div>

          {(videoUrls.videoUrl1 || videoUrls.videoUrl2) && (
            <div className="how-it-works-videos">
              {videoUrls.videoUrl1 && (
                <div className="video-wrapper">
                  <iframe
                    src={videoUrls.videoUrl1}
                    title="OfferteRunner video 1"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              {videoUrls.videoUrl2 && (
                <div className="video-wrapper">
                  <iframe
                    src={videoUrls.videoUrl2}
                    title="OfferteRunner video 2"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Odoo Section */}
      <section id="odoo" className="landing-section">
        <div className="landing-container">
          <div className="odoo-block">
            <div className="odoo-text">
              <div className="odoo-badge">Kernintegratie</div>
              <h2>Gebouwd op Odoo</h2>
              <p>
                OfferteRunner is ontworpen om naadloos samen te werken met Odoo. Je producten, klanten
                en offertes leven in Odoo &mdash; OfferteRunner voegt daar een krachtige offertewizard aan toe.
              </p>
              <ul className="odoo-list">
                <li>
                  <strong>Producten synchroniseren</strong> &mdash; Automatische sync van je Odoo-productcatalogus
                </li>
                <li>
                  <strong>Klanten uit Odoo</strong> &mdash; Zoek en selecteer bestaande relaties of maak nieuwe aan
                </li>
                <li>
                  <strong>Offertebijlage</strong> &mdash; PDF wordt als product opgeslagen in Odoo met bijlage
                </li>
                <li>
                  <strong>Multi-bedrijf</strong> &mdash; Werk met meerdere Odoo-bedrijven vanuit één dashboard
                </li>
              </ul>
            </div>
            <div className="odoo-visual">
              <div className="odoo-flow">
                <div className="odoo-flow-item">
                  <div className="odoo-flow-icon">🏢</div>
                  <span>Odoo CRM</span>
                </div>
                <div className="odoo-flow-arrow">→</div>
                <div className="odoo-flow-item active">
                  <div className="odoo-flow-icon">⚡</div>
                  <span>OfferteRunner</span>
                </div>
                <div className="odoo-flow-arrow">→</div>
                <div className="odoo-flow-item">
                  <div className="odoo-flow-icon">📄</div>
                  <span>PDF Offerte</span>
                </div>
                <div className="odoo-flow-arrow">→</div>
                <div className="odoo-flow-item">
                  <div className="odoo-flow-icon">🔄</div>
                  <span>Terug naar Odoo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API Section */}
      <section id="api" className="landing-section alt-bg">
        <div className="landing-container">
          <div className="section-header">
            <h2>REST API voor externe integratie</h2>
            <p>Ontvang offerte-aanvragen van je website, webshop of elk ander systeem.</p>
          </div>

          <div className="api-showcase">
            <div className="api-showcase-text">
              <h3>Automatisch aanvragen ontvangen</h3>
              <p>
                Elk bedrijf krijgt automatisch een unieke API-sleutel. Externe systemen sturen
                een POST-request met producten en klantgegevens. De aanvraag verschijnt direct
                in het Aanvragendashboard, klaar om met één klik te verwerken tot offerte.
              </p>
              <ul className="api-features-list">
                <li>Automatische API-sleutel per bedrijf</li>
                <li>Producten koppelen op product-ID of vrije invoer</li>
                <li>Klant- en contactgegevens worden vooringevuld</li>
                <li>Opmerkingen worden doorgezet naar de offertewizard</li>
              </ul>
            </div>
            <div className="api-showcase-code">
              <div className="code-header">
                <span className="code-method">POST</span>
                <span className="code-path">/api/offer-requests/:subscriberId</span>
              </div>
              <pre className="code-block">{`{
  "aanvrager": "Website formulier",
  "opmerkingen": "Graag snel offerte.",
  "products": [
    { "product_id": "uuid-product" },
    { "name": "Extra", "price": 500 }
  ],
  "customer": {
    "name": "Bedrijf B.V.",
    "email": "info@bedrijf.nl",
    "contact_name": "Jan de Vries"
  }
}`}</pre>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="landing-section">
        <div className="landing-container">
          <div className="section-header">
            <h2>Eenvoudige, transparante prijzen</h2>
            <p>Probeer OfferteRunner 14 dagen gratis. Daarna een jaarlicentie zonder verrassingen.</p>
          </div>

          <div className="pricing-cards">
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h3>Proefperiode</h3>
                <div className="pricing-price">&euro; 0</div>
                <span className="pricing-period">14 dagen</span>
              </div>
              <ul className="pricing-features">
                <li>Volledige functionaliteit</li>
                <li>Odoo-synchronisatie</li>
                <li>AI-teksten genereren</li>
                <li>PDF-offertes maken</li>
                <li>Geen creditcard nodig</li>
              </ul>
              <button className="btn-landing-secondary" onClick={() => navigate('/login')}>
                Gratis uitproberen
              </button>
            </div>

            <div className="pricing-card featured">
              <div className="pricing-badge">Meest gekozen</div>
              <div className="pricing-card-header">
                <h3>Jaarlicentie</h3>
                <div className="pricing-price">&euro; 275</div>
                <span className="pricing-period">per jaar, excl. btw</span>
              </div>
              <ul className="pricing-features">
                <li>Alles uit de proefperiode</li>
                <li>Onbeperkt offertes maken</li>
                <li>Onbeperkt PDF-downloads</li>
                <li>API voor externe aanvragen</li>
                <li>Eigen offertetemplates aanmaken</li>
                <li>Persoonlijke ondersteuning</li>
              </ul>
              <button className="btn-landing-primary" onClick={() => navigate('/login')}>
                Aan de slag
              </button>
            </div>

            <div className="pricing-card">
              <div className="pricing-card-header">
                <h3>Template op maat</h3>
                <div className="pricing-price">&euro; 75</div>
                <span className="pricing-period">per deeltemplate, eenmalig</span>
              </div>
              <ul className="pricing-features">
                <li>Professioneel opgemaakt template</li>
                <li>Op basis van uw huisstijl</li>
                <li>Per onderwerp / dienst</li>
                <li>Direct bruikbaar in uw offertes</li>
                <li>Of maak ze zelf aan, gratis</li>
              </ul>
              <button className="btn-landing-secondary" onClick={() => navigate('/login')}>
                Neem contact op
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta-section">
        <div className="landing-container">
          <div className="cta-block">
            <h2>Klaar om professionele offertes te maken?</h2>
            <p>Start vandaag nog met OfferteRunner en ervaar hoe makkelijk het kan zijn.</p>
            <button className="btn-landing-primary large" onClick={() => navigate(user ? '/' : '/login')}>
              {user ? 'Naar de app' : 'Aan de slag'}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/logo-offerterunner.png" alt="OfferteRunner" className="footer-logo-img" />
              <p>De slimme offertetool, gebouwd op Odoo.</p>
              <p className="footer-company-info">
                Een product van <a href="https://ploko.nl" target="_blank" rel="noopener noreferrer">Ploko.nl</a>
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-col">
                <h4>Product</h4>
                <a href="#features">Functionaliteiten</a>
                <a href="#how-it-works">Hoe het werkt</a>
                <Link to="/blog">Blog</Link>
                <a href="#api">API</a>
              </div>
              <div className="footer-col">
                <h4>Integraties</h4>
                <a href="#odoo">Odoo CRM</a>
                <a href="#api">REST API</a>
              </div>
              <div className="footer-col">
                <h4>Ploko.nl</h4>
                <span>H. Kamerlingh Onnesweg 5</span>
                <span>Alphen a/d Rijn</span>
                <span>support@ploko.nl</span>
                <a href="https://ploko.nl" target="_blank" rel="noopener noreferrer">ploko.nl</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} <a href="https://ploko.nl" target="_blank" rel="noopener noreferrer">Ploko.nl</a> &mdash; OfferteRunner is gebouwd met Odoo, React &amp; AI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
